import { useCallback, useEffect, useRef, useState } from 'react';

type UseTapActivatorOptions = {
  threshold: number;
  timeoutMs?: number;
};

// Detects a burst of taps/clicks within a time window
export function useTapActivator({ threshold, timeoutMs = 1000 }: UseTapActivatorOptions) {
  const [count, setCount] = useState(0);
  const [isActivated, setIsActivated] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    setCount(0);
    setIsActivated(false);
  }, [clearTimer]);

  const handleTap = useCallback(() => {
    if (isActivated) return;

    setCount(prev => {
      const next = prev + 1;

      if (next >= threshold) {
        clearTimer();
        setIsActivated(true);
        return 0;
      }

      clearTimer();
      timerRef.current = setTimeout(() => {
        setCount(0);
        timerRef.current = null;
      }, timeoutMs);

      return next;
    });
  }, [clearTimer, isActivated, threshold, timeoutMs]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { isActivated, handleTap, reset, count };
}
